import React, { useState } from "react";
import {
  SettingsSection,
  SettingsSectionHeader,
  SettingsSectionContent,
} from "components/Graphs/SettingsSection";
import { Button } from "components/Graphs/Button";
import {
  TableRoot,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "components/Graphs/Table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@kui-react/spinner";
import { InfoTooltip } from "components/Graphs/InfoTooltip";
import { OrgApiKeyType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/org_api_key_pb";
import { useOrgAPIKeys, type KeyRole, type OrgAPIKey } from "./useOrgAPIKeys";
import CreateAPIKeyModal from "./CreateAPIKeyModal";
import KeyCreatedModal from "./KeyCreatedModal";
import EditAPIKeyModal from "./EditAPIKeyModal";
import DeleteConfirmModal from "./DeleteConfirmModal";

function formatDate(ts?: string): string {
  if (!ts) return "Never";
  const date = new Date(ts);
  if (isNaN(date.getTime())) return "Never";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isExpired(ts?: string): boolean {
  if (!ts) return false;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

function formatRelative(ts?: string): string {
  if (!ts) return "Never";
  const date = new Date(ts);
  if (isNaN(date.getTime())) return "Never";
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return formatDate(ts);
}

function formatRoleNames(roleIds: string[] | undefined, roles: KeyRole[]) {
  if (!roleIds?.length) return "No role";
  const roleNameById = new Map(
    roles.map((role) => [role.id, role.displayName]),
  );
  return roleIds.map((roleId) => roleNameById.get(roleId) ?? roleId).join(", ");
}

function formatKeyType(keyType: OrgApiKeyType | undefined): string {
  switch (keyType) {
    case OrgApiKeyType.USER:
      return "Personal";
    default:
      return "Unknown";
  }
}

const APIKeys: React.FC = () => {
  const {
    keys,
    availableRoles,
    isLoading,
    isMutating,
    error,
    createKey,
    updateKey,
    deleteKey,
  } = useOrgAPIKeys();

  const [createOpen, setCreateOpen] = useState(false);
  const [createdPlaintext, setCreatedPlaintext] = useState("");
  const [createdOrgId, setCreatedOrgId] = useState("");
  const [createdModalOpen, setCreatedModalOpen] = useState(false);
  const [editKey, setEditKey] = useState<OrgAPIKey | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgAPIKey | null>(null);

  const handleCreate = async (req: Parameters<typeof createKey>[0]) => {
    const res = await createKey(req);
    setCreateOpen(false);
    setCreatedPlaintext(res.plaintextValue);
    setCreatedOrgId(req.orgId);
    setCreatedModalOpen(true);
  };

  return (
    <>
      <SettingsSection>
        <SettingsSectionHeader
          title="API Keys"
          description="Create and manage API keys for programmatic access."
          action={
            <Button
              variant="primary"
              disabled={isMutating}
              onClick={() => setCreateOpen(true)}
            >
              Create API Key
            </Button>
          }
        />
        <SettingsSectionContent>
          {error && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-red-900 bg-red-950/40 p-3 text-sm text-red-300"
            >
              {error}
            </div>
          )}
          {isMutating && !isLoading && (
            <div className="mb-4 text-sm text-secondary">
              Updating API keys...
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-8 text-secondary text-sm">
              No API keys yet. Create one to get started.
            </div>
          ) : (
            <TableRoot>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Key</TableHeaderCell>
                    <TableHeaderCell>
                      <span className="inline-flex items-center">
                        Type
                        <InfoTooltip text="Personal API Keys act on behalf of the creator, with that user's permissions" />
                      </span>
                    </TableHeaderCell>
                    <TableHeaderCell>Roles</TableHeaderCell>
                    <TableHeaderCell>Expires</TableHeaderCell>
                    <TableHeaderCell>Last Used</TableHeaderCell>
                    <TableHeaderCell className="w-10" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((key) => {
                    const expired = isExpired(key.expiresAt);
                    return (
                      <TableRow key={key.id}>
                        <TableCell className="font-medium">
                          {key.name}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs text-secondary font-mono">
                            {key.keyPrefix}...
                          </code>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-md bg-zinc-800 px-2 py-0.5 text-xs text-secondary">
                            {formatKeyType(key.keyType)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-md bg-zinc-800 px-2 py-0.5 text-xs text-secondary">
                            {formatRoleNames(key.roleIds, availableRoles)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-secondary">
                          {expired ? (
                            <span className="inline-flex items-center rounded-md bg-red-950 px-2 py-0.5 text-xs text-red-300">
                              Expired
                            </span>
                          ) : (
                            formatDate(key.expiresAt)
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-secondary">
                          {formatRelative(key.lastUsedAt)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1 rounded hover:bg-zinc-800"
                                aria-label={`Actions for ${key.name}`}
                              >
                                <EllipsisHorizontalIcon className="w-5 h-5 text-secondary" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!expired && (
                                <DropdownMenuItem
                                  onClick={() => setEditKey(key)}
                                >
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-red-400"
                                onClick={() => setDeleteTarget(key)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableRoot>
          )}
        </SettingsSectionContent>
      </SettingsSection>

      <CreateAPIKeyModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        availableRoles={availableRoles}
        onSubmit={handleCreate}
      />

      <KeyCreatedModal
        open={createdModalOpen}
        plaintextValue={createdPlaintext}
        orgId={createdOrgId}
        onClose={() => {
          setCreatedModalOpen(false);
          setCreatedPlaintext("");
          setCreatedOrgId("");
        }}
      />

      <EditAPIKeyModal
        open={!!editKey}
        onOpenChange={(open) => {
          if (!open) setEditKey(null);
        }}
        apiKey={editKey}
        availableRoles={availableRoles}
        onSubmit={updateKey}
      />

      <DeleteConfirmModal
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        keyName={deleteTarget?.name ?? ""}
        onConfirm={async () => {
          if (deleteTarget) await deleteKey(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </>
  );
};

export default APIKeys;
